import React from 'react';
import _ from 'lodash';
import moment from 'moment-timezone';

import { getBillingDashboardPageBreadcrumb } from '../Billing/BillingDashboardPage';

import PageHeader from '../../Common/PageHeader';
import NavigationBreadcrumbs from '../../Common/NavigationBreadcrumbs';

import Field from '../../Fields/Field';
import InputTextField from '../../Fields/InputTextField';
import InputSelectField from '../../Fields/InputSelectField';
import DataTable from '../../Fields/DataTable';

import Parker from '../../../Entities/Parker';
import { formatPhoneNumber } from '../../../Entities/util';

import {
  queryListParkers,
  queryGetParkerReport
} from '../../../Queries';

import { formatDateForDisplay } from '../../../Utils/date';
import { getFacility } from '../../../Utils/facility';

import './ParkerListPage.scss';
import featureFlags from '../../../featureFlags';

const oldParkerQueryFields =[
  'total',
  'limit',
  'offset',
  { 'parkers': [
    'id',
    'rate_id',
    'subscription_id',
    'suspension_status',
    'termination_status',
    'first_name',
    'last_name',
    'email',
    'address',
    'city',
    'state',
    'zip',
    'phone',
    'parking_reason',
    'barcode',
    'group',
    'passback',
    'reserved_space',
    { 'payor': [
      'id',
      'readable_id',
      'billing_firstname',  // for columns titled "PC __"
      'billing_lastname',
      'info_name' // for columns titled "billing name"
    ] },
    { 'rate': [
      'id',
      'name'
    ] },
    { 'parker_status_history': [
      'action',
      'start_date'
    ] },
  ] }
];

const parkerQueryFields = featureFlags.SUBSCRIPTION_NAME ? [
  'total',
  'limit',
  'offset',
  { 'parkers': [
    'id',
    'rate_id',
    'subscription_id',
    'suspension_status',
    'termination_status',
    'first_name',
    'last_name',
    'email',
    'address',
    'city',
    'state',
    'zip',
    'phone',
    'parking_reason',
    'barcode',
    'group',
    'reserved_space',
    'passback',
    { 'payor': [
      'id',
      'readable_id',
      'billing_firstname',  // for columns titled "PC __"
      'billing_lastname',
      'info_name' // for columns titled "billing name"
    ] },
    {'subscription': [
      'subscription_name', 
      featureFlags.ACCOUNT_ID ? 'account_id' : ''
      ]
    },
    { 'rate': [
      'id',
      'name',
      featureFlags.ACCOUNT_ID ? 'display_price' : ''
    ] },
    { 'parker_status_history': [
      'action',
      'start_date'
    ] },

  ] }
] : oldParkerQueryFields;

const oldParkerTableColumns = [
  { id: 'last_name', heading: 'Last Name' },
  { id: 'first_name', heading: 'First Name' },
  { id: 'payor_id', heading: 'Sub. ID' },
  { id: 'payor_billing_name', heading: 'PC Name' },
  { id: 'billing_name', heading: 'Billing Name' },
  { id: 'product', heading: 'Product' },
  { id: 'phone', heading: 'Phone' },
  { id: 'email', heading: 'Email' },
  { id: 'reserved_space' , heading: 'Reserved Space' },
  { id: 'termination_date', heading: 'Termination Date' },
  { id: 'passback', heading: 'Passback' },
  { id: 'actions', heading: null }
];

const newParkerTableColumns = featureFlags.SUBSCRIPTION_NAME ? [
  { id: 'last_name', heading: 'Last Name' },
  { id: 'first_name', heading: 'First Name' },
  { id: 'payor_id', heading: 'Sub. ID' },
  { id: 'subscription_name', heading: 'Sub. Name' },
  { id: 'billing_name', heading: 'Billing Name' },
  { id: 'product', heading: 'Product' },
  { id: 'phone', heading: 'Phone' },
  { id: 'email', heading: 'Email' },
  { id: 'reserved_space' , heading: 'Reserved Space' },
  { id: 'termination_date', heading: 'Termination Date' },
  { id: 'passback', heading: 'Passback' },
  { id: 'actions', heading: null }
] : oldParkerTableColumns;

const parkerTableColumns = featureFlags.ACCOUNT_ID ? [
  { id: 'last_name', heading: 'Last Name' },
  { id: 'first_name', heading: 'First Name' },
  { id: 'account_id', heading: 'Acct. ID' },
  { id: 'payor_id', heading: 'Sub. ID' },
  { id: 'subscription_name', heading: 'Sub. Name' },
  { id: 'product', heading: 'Product' },
  { id: 'rate', heading: 'Rate' },
  { id: 'phone', heading: 'Phone' },
  { id: 'email', heading: 'Email' },
  { id: 'reserved_space' , heading: 'Reserved Space' },
  { id: 'termination_date', heading: 'Termination Date' },
  { id: 'passback', heading: 'Passback' }
] : featureFlags.SUBSCRIPTION_NAME ? newParkerTableColumns : oldParkerTableColumns;

class ParkerListPage extends React.Component {
  state = {
    facility: null,
    parkers: {
      raw: null,
      formatted: null
    },
    parkersFilter: {
      initializationComplete: false,
      facility: null,
      inputs: {
        id: null,
        suspension_status: 'ALL_ACTIVE',
        termination_status: 'ALL_ACTIVE',
        first_name: null,
        last_name: null,
        payor_id: null,
        subscription_name: null,
        payor_billing_name: null,  //TO DO - Remove when removing OVERPAYMENT feature flag
        account_id: null
      },
      fieldConfiguration: {
        suspension_status: {
          options: []
        },
        termination_status: {
          options: []
        }
      }
    }
  };

  parkerTableColumns = parkerTableColumns;

  parkerQueryOptions = {
    sortedBy: null,
    limit: null,
    total: null,
    offset: null
  };

  facilityId;

  constructor(props) {
    super(props);

    const urlSearchParams = new URLSearchParams(window.location.search);

    const facilityId = urlSearchParams.get('facility');
    this.facilityId = (facilityId !== null) ? facilityId : null;
  }


  async componentDidMount() {
    if (this.facilityId) {
      const facility = await getFacility(this.facilityId);
      this.setState({ facility });
    }

    const data = await Parker.getFieldAllowedValues([ 'suspension_status', 'termination_status' ]);
    const fieldConfiguration = {
      ...this.state.parkersFilter.fieldConfiguration,
      ...data
    };
    const parkersFilter = {
      ...this.state.parkersFilter,
      fieldConfiguration,
      initializationComplete: true
    };

    parkersFilter.fieldConfiguration.suspension_status.options = parkersFilter.fieldConfiguration.suspension_status.options.filter(option => option.value !== 'PENDING');
    parkersFilter.fieldConfiguration.termination_status.options = parkersFilter.fieldConfiguration.termination_status.options.filter(option => option.value !== 'PENDING');

    this.setState({ parkersFilter }, () => {
      this.getParkers();
    });
  }

  // -- Parker Data Actions

  getParkers() {
    const options = this.getParkerQueryOptions();

    queryListParkers(options, parkerQueryFields).then(res => {
      const raw = res.data.listParkers.parkers;
      const formatted = this.getFormattedParkerDataForDataTable(raw);

      let parkers = { ...this.state.parkers, raw, formatted };
      this.setState({ ...this.state, parkers });
    });
  }

  getParkerDataExport() {
    queryGetParkerReport(this.getParkerQueryOptions())
      .then(response => response.blob())
      .then(csvBlob => {
        if (navigator.msSaveBlob) {
          navigator.msSaveBlob(csvBlob, 'parkers.csv');
        }
        else {
          let linkElement = document.createElement('a');
          let url = window.URL;
          let objectUrl;
          linkElement.type = 'text/csv';
          linkElement.download = 'parkers.csv';
          linkElement.href = objectUrl = url.createObjectURL(csvBlob);
          linkElement.dispatchEvent(new MouseEvent('click'));
          setTimeout(url.revokeObjectURL(objectUrl));
        }
      });
  }

  getFormattedParkerDataForDataTable(rawData) {
    let formattedData = [];

    for (let parker of rawData) {
      let row = [];

      for (let column of this.parkerTableColumns) {
        switch(column.id) {
          case 'actions':
            row.push(`<a href="admin.php?page=parkchirp_parker&parker=${parker.id}">Manage</a>`);
            break;
          case 'last_name':
            row.push(featureFlags.ACCOUNT_ID ? `<a href="admin.php?page=parkchirp_parker&parker=${parker.id}&facility=${this.facilityId}">${parker.last_name}</a>`: parker.last_name);
            break;
          case 'first_name':
            row.push(featureFlags.ACCOUNT_ID ? `<a href="admin.php?page=parkchirp_parker&parker=${parker.id}&facility=${this.facilityId}">${parker.first_name}</a>` : parker.first_name);
            break;
          case 'account_id':
            row.push(`AC-${parker.subscription.account_id}`);
            break;
          case 'subscription_name':
            row.push(parker.subscription.subscription_name);
            break;
          case 'payor_id':
            row.push(`<a href="admin.php?page=parkchirp_subscription&subscription=${parker.subscription_id}#/payor">${parker.payor.readable_id}</a>`);
            break;
          case 'rate':
            const displayPrice = `$${parker.rate.display_price}`
            let terminationDate = null;
            parker.parker_status_history.forEach((item) => {
              if (item.action === "TERMINATE") {
                terminationDate = moment(item.start_date);
              }
            });
            row.push(terminationDate?.isSameOrBefore(moment(), 'day') ? '-' : displayPrice);
            break;
          case 'phone':
            row.push((parker[column.id] !== null) ? formatPhoneNumber(parker[column.id]) : '');
            break;
          case 'termination_date':
            if(parker[column.id] === null) row.push(null);
            else {
              let terminationDate = null;
              parker.parker_status_history.forEach((item) => {
                if (item.action === "TERMINATE") {
                  terminationDate = moment(item.start_date);
                }
              });
              terminationDate = terminationDate ? formatDateForDisplay(terminationDate) : null;
              row.push(terminationDate);
            }
            break;
          case 'payor_billing_name':
            row.push(`${parker.payor.billing_firstname} ${parker.payor.billing_lastname}`);
            break;
          case 'product':
            row.push(parker.rate.name);
            break;
          case 'billing_name':
            row.push((parker[column.id] !== null) ? `${parker.payor.info_name}` : '');
            break;
          case 'passback':
            const passback = parker[column.id];
            if(passback === null) row.push('')
            else row.push(passback ? 'On' : 'Off')
            break;  
          default:
            row.push(parker[column.id]);
        }
      }

      formattedData.push(row);
    }

    return formattedData;
  }

  getParkerQueryOptions() {
    const { parkersFilter } = this.state;

    const queryOptions = {};

    const filters = [];
    for (let input in parkersFilter.inputs) {
      if (parkersFilter.inputs[input] === null) continue;

      switch (input) {
        case 'id':
          filters.push({
            model: 'PARKER',
            field: input,
            op: 'EQ',
            value: parkersFilter.inputs[input]
          });
          break;
        case 'suspension_status':
          let value = parkersFilter.inputs[input];
          let allValues = ['ACTIVE', 'SUSPENSION_PENDING', 'SUSPENDED', 'TERMINATED'];
          let allActiveValues = ['ACTIVE', 'SUSPENSION_PENDING'];

          let operation = {
            op: 'IN',
            value: (value==='ALL') ? allValues : (value==='ALL_ACTIVE')  ? allActiveValues : [value],
          }
          filters.push({
            model: 'PARKER',
            field: input,
            ...operation
          });
          break;
        case 'termination_status':
          value = parkersFilter.inputs[input];
          allValues = ['ACTIVE', 'TERMINATION_PENDING', 'TERMINATED'];
          allActiveValues = ['ACTIVE', 'TERMINATION_PENDING'];

          operation = {
            op: 'IN',
            value: (value==='ALL') ? allValues : (value==='ALL_ACTIVE')  ? allActiveValues : [value],
          }
          filters.push({
            model: 'PARKER',
            field: input,
            ...operation
          });
          break;
        case 'first_name':
        case 'last_name':
          filters.push({
            model: 'PARKER',
            field: input,
            op: 'SUBSTRING',
            value: parkersFilter.inputs[input]
          });
          break;
        case 'account_id':
          filters.push({
            model: 'SUBSCRIPTION',
            field: 'account_id',
            op: 'SUBSTRING',
            value: parkersFilter.inputs[input].trim().replace(/\D+/g, '')
          });
          break;
        case 'subscription_name':
          filters.push({
            model: 'SUBSCRIPTION',
            field: 'subscription_name',
            op: 'EQ',
            value: parkersFilter.inputs[input].trimStart()
          });
          break;
        case 'payor_id':
          filters.push({
            model: 'PAYOR',
            field: 'readable_id',
            op: 'EQ',
            value: parkersFilter.inputs[input]
          });
          break;
        case 'payor_billing_name':
          filters.push({
            model: 'PAYOR',
            field: 'info_name',
            op: 'EQ',
            value: parkersFilter.inputs[input]
          });
          break;
        default:
      }
    }
    if (this.facilityId !== null) {
      filters.push({
        model: 'BILLING_PROFILE',
        field: 'facility_id',
        op: 'EQ',
        value: this.facilityId
      });
    }
    if (filters.length) {
      queryOptions.filters = filters;
    }

    if (this.parkerQueryOptions.sortedBy &&
      this.parkerQueryOptions.sortedBy.columnId !== '' &&
      this.parkerQueryOptions.sortedBy.direction !== ''
    ) {
      switch (this.parkerQueryOptions.sortedBy.columnId) {
        case 'subscription_name':
          queryOptions.order = {
            model: 'SUBSCRIPTION',
            field: 'subscription_name',
            direction: this.parkerQueryOptions.sortedBy.direction
          };
          break;
        case 'account_id':
          queryOptions.order = {
            model: 'SUBSCRIPTION',
            field: 'account_id',
            direction: this.parkerQueryOptions.sortedBy.direction
          };
          break;
        case 'payor_id':
          queryOptions.order = {
            model: 'PAYOR',
            field: 'readable_id',
            direction: this.parkerQueryOptions.sortedBy.direction
          };
          break;
        case 'billing_name':
          queryOptions.order = {
            model: 'PAYOR',
            field: 'info_name',
            direction: this.parkerQueryOptions.sortedBy.direction
          };
          break;
        case 'product':
          queryOptions.order = {
            model: 'RATE',
            field: 'name',
            direction: this.parkerQueryOptions.sortedBy.direction
          };
          break; 
        default:
          queryOptions.order = {
            field: this.parkerQueryOptions.sortedBy.columnId,
            direction: this.parkerQueryOptions.sortedBy.direction
          };
      }
    }

    if (this.parkerQueryOptions.limit !== null) {
      queryOptions.limit = this.parkerQueryOptions.limit;
    }

    if (this.parkerQueryOptions.offset !== null) {
      queryOptions.offset = this.parkerQueryOptions.offset;
    }

    return queryOptions;
  }


  // -- Parkers Filter Form Actions & Event Handlers

  _getParkersFilterInputOnChange(inputId) {
    return value => {
      let inputs = { ...this.state.parkersFilter.inputs, [inputId]: value };
      let parkersFilter = { ...this.state.parkersFilter, inputs };
      this.setState({ parkersFilter });
    };
  }

  resetParkersFilterForm() {
    const inputs = {};
    for (let input in this.state.parkersFilter.inputs) {
      switch (input) {
        case 'suspension_status':
        case 'termination_status':
          inputs[input] = 'ALL_ACTIVE';
          break;
        default:
          inputs[input] = null;
      }
    }
    const parkersFilter = { ...this.state.parkersFilter, inputs };
    this.setState({ parkersFilter }, this.getParkers);
  }

  handleParkerFilterButtonClick = () => {
    this.getParkers();
  }

  handleParkerResetButtonClick = () => {
    this.resetParkersFilterForm();
  }


  // -- Parker Data Table Event Handlers

  handleParkerDataTableExportToCSVClick = () => {
    this.getParkerDataExport();
  }

  handleParkerDataTableSortRequest = data => {
    this.parkerQueryOptions.sortedBy = data;
    if(data?.columnId === 'rate') {
      let rawValues = _.cloneDeep(this.state.parkers.raw);
      const values = rawValues.sort((a, b) => {
        let terminationDateA = null;
        let terminationDateB = null;
        a.parker_status_history.forEach((item) => {
          if (item.action === "TERMINATE") {
            terminationDateA = moment(item.start_date);
          }
        });
        b.parker_status_history.forEach((item) => {
          if (item.action === "TERMINATE") {
            terminationDateB = moment(item.start_date);
          }
        });
        const rateAmountA = a[data.columnId].display_price.replace(/,/g, '');
        const rateAmountB = b[data.columnId].display_price.replace(/,/g, '');
        const displayPriceA = terminationDateA?.isSameOrBefore(moment(), 'day') ? 0 : rateAmountA === '0.00' ? '0.01' : rateAmountA;
        const displayPriceB = terminationDateB?.isSameOrBefore(moment(), 'day') ? 0 : rateAmountB === '0.00' ? '0.01' : rateAmountB;
        const diff = parseFloat(displayPriceA) - parseFloat(displayPriceB);
        return (data.direction === 'ASC' ? diff : -diff);
      });
      const formatted = this.getFormattedParkerDataForDataTable(values)
      let parkers = { raw: rawValues, formatted };
      this.setState({ parkers });
    } else {
      this.getParkers();
    }
  }

  handleParkerDataTableLimitChange = data => {
    this.parkerQueryOptions.limit = data;
    this.parkerQueryOptions.offset = null;
    this.getParkers();
  }

  handleParkerDataTablePageChange = data => {
    let offset = ((data - 1) * this.parkerQueryOptions.limit);
    this.parkerQueryOptions.offset = offset;
    this.getParkers();
  }



  // -- Rendering


  renderNavigationBreadcrumbs() {
    const breadcrumbs = [];
    breadcrumbs.push(getBillingDashboardPageBreadcrumb(false, this.facilityId));
    breadcrumbs.push(getParkerListPageBreadcrumb(true));

    return (
      <NavigationBreadcrumbs breadcrumbs={breadcrumbs} />
    );
  }


  renderPageHeader() {
    const {
      facility
    } = this.state;

    const heading = 'Parker Roster';
    const subheading = (facility === null) ? null :
      facility.fields.facility_name.value;

    return (
      <PageHeader heading={heading}
        subheading={subheading}
      />
    );
  }


  render() {
    return (
      <div className="parkchirp--page parkchirp--page--parker-roster">
        {this.renderNavigationBreadcrumbs()}
        {this.renderPageHeader()}
        {this.renderFilterForm()}
        {this.renderParkerTable()}
      </div>
    );
  }

  renderFilterForm() {
    const { parkersFilter } = this.state;

    return (
      <div className="parkchirp--parker-roster--filter-form">
        <h2>Filter</h2>
        <div className="parkchirp--parker-roster--filter-inputs">
          <div className="parkchirp--parker-roster--filter-input-block">
            <Field label="First Name"
              id="parkchirp--parker-roster-filter--first-name"
              layout="columns">
              <InputTextField inputId="parkchirp--parker-roster-filter--first-name"
                value={parkersFilter.inputs.first_name}
                onChange={this._getParkersFilterInputOnChange('first_name')}
                editMode={parkersFilter.initializationComplete}
              />
            </Field>
            <Field label="Last Name"
              id="parkchirp--parker-roster-filter--last-name"
              layout="columns">
              <InputTextField inputId="parkchirp--parker-roster-filter--last-name"
                value={parkersFilter.inputs.last_name}
                onChange={this._getParkersFilterInputOnChange('last_name')}
                editMode={parkersFilter.initializationComplete}
              />
            </Field>
          </div>
          <div className="parkchirp--parker-roster--filter-input-block">
            <Field label="Suspension Status"
              id="parkchirp--parker-roster-filter--suspension-status"
              layout="columns">
              <InputSelectField inputId="parkchirp--parker-roster-filter--suspension-status"
                options={[ { value: 'ALL', label: 'All' }, { value: 'ALL_ACTIVE', label: 'Active & Suspension Pending' }, ...parkersFilter.fieldConfiguration.suspension_status.options ]}
                value={parkersFilter.inputs.suspension_status}
                onChange={this._getParkersFilterInputOnChange('suspension_status')}
                editMode={parkersFilter.initializationComplete}
              />
            </Field>
            <Field label="Termination Status"
              id="parkchirp--parker-roster-filter--termination-status"
              layout="columns">
              <InputSelectField inputId="parkchirp--parker-roster-filter--termination-status"
                options={[ { value: 'ALL', label: 'All' }, { value: 'ALL_ACTIVE', label: 'Active & Termination Pending' }, ...parkersFilter.fieldConfiguration.termination_status.options ]}
                value={parkersFilter.inputs.termination_status}
                onChange={this._getParkersFilterInputOnChange('termination_status')}
                editMode={parkersFilter.initializationComplete}
              />
            </Field>
          </div>
          <div className="parkchirp--parker-roster--filter-input-block">
            {featureFlags.ACCOUNT_ID && 
              <Field label="Acct ID"
                id="parkchirp--parker-roster-filter--account-id"
                layout="columns">
                <InputTextField inputId="parkchirp--parker-roster-filter--account-id"
                  value={parkersFilter.inputs.account_id}
                  onChange={this._getParkersFilterInputOnChange('account_id')}
                  editMode={parkersFilter.initializationComplete}
                />
              </Field>
            }
            <Field label="Sub. ID"
              id="parkchirp--parker-roster-filter--payor-id"
              layout="columns">
              <InputTextField inputId="parkchirp--parker-roster-filter--payor-id"
                value={parkersFilter.inputs.payor_id}
                onChange={this._getParkersFilterInputOnChange('payor_id')}
                editMode={parkersFilter.initializationComplete}
              />
            </Field>

            {!featureFlags.SUBSCRIPTION_NAME &&
              <Field label="Billing Name"
                id="parkchirp--parker-roster-filter--billing-name"
                layout="columns">
                <InputTextField inputId="parkchirp--parker-roster-filter--billing-name"
                  value={parkersFilter.inputs.payor_billing_name}
                  onChange={this._getParkersFilterInputOnChange('payor_billing_name')}
                  editMode={parkersFilter.initializationComplete}
                />
              </Field>
            }
            {featureFlags.SUBSCRIPTION_NAME &&
              <Field label="Sub. Name"
                id="parkchirp--parker-roster-filter----sub-name"
                layout="columns">
                <InputTextField inputId="parkchirp--parker-roster-filter--sub-name"
                  value={parkersFilter.inputs.subscription_name}
                  onChange={this._getParkersFilterInputOnChange('subscription_name')}
                  editMode={parkersFilter.initializationComplete}
                />
              </Field>
            }

          </div>
        </div>
        <div className="parkchirp--parker-roster--filter-actions">
          <button type="button"
            className="button button-primary parkchirp--parker-roster--filter-action"
            onClick={this.handleParkerFilterButtonClick}>
            Filter
          </button>
          <button type="button"
            className="button parkchirp--parker-roster--filter-action"
            onClick={this.handleParkerResetButtonClick}>
            Reset
          </button>
        </div>
      </div>
    );
  }

  renderParkerTable() {
    const { parkers } = this.state;

    if (parkers.formatted === null) {
      return;
    }

    return (
      <div className="parkchirp--parker-roster--parker-table">
        {(parkers.raw.length > 0) &&
          <div className="parkchirp--parker-roster--parker-table-actions">
            <div className="parkchirp--parker-roster--parker-table-action">
              <button
                type="button"
                className='button button-primary'
                onClick={this.handleParkerDataTableExportToCSVClick}
              >Export to CSV</button>
            </div>
          </div>
        }
        <DataTable
          id="parker-roster"
          rows={parkers.formatted}
          columns={this.parkerTableColumns}
          overallRowCount={this.parkerQueryOptions.total}
          currentOffset={this.parkerQueryOptions.offset}
          currentLimit={this.parkerQueryOptions.limit}
          sortedBy={this.parkerQueryOptions.sortedBy}
          onSortRequest={this.handleParkerDataTableSortRequest}
          onLimitChange={this.handleParkerDataTableLimitChange}
          onPageChange={this.handleParkerDataTablePageChange}
        />
      </div>
    );
  }
}
export default ParkerListPage;


export function getParkerListPageBreadcrumb(currentPage = false, facilityId = null) {
  const title = 'Parker Roster';
  const href = (currentPage) ? null :
    `?page=parkchirp_parkers${(facilityId !== null) ? `&facility=${facilityId}` : ''}`;

  return { title, href };
}